import mixpanel from 'mixpanel-browser';
import { createFileLogger } from '../../utils/logger';

const log = createFileLogger('MIXPANEL_SERVICE', { enabled: false });

interface MixpanelConfig {
  token?: string;
  projectToken?: string; // Legacy support
  debug?: boolean;
  track_pageview?: boolean;
  persistence?: 'localStorage' | 'cookie';
  api_host?: string;
  environment?: 'development' | 'production';
  sessionRecording?: {
    enabled: boolean;
    debugMode: boolean;
  };
  debugMode?: boolean;
}

class MixpanelService {
  private isInitialized = false;
  private isReady = false;

  async init(config: MixpanelConfig): Promise<void> {
    if (this.isInitialized) {
      log.warn('Mixpanel already initialized');
      return;
    }

    try {
      // Initialize Mixpanel with config
      const token = config.token || config.projectToken;
      if (!token) {
        throw new Error('Mixpanel token is required');
      }
      
      mixpanel.init(token, {
        debug: config.debug || config.debugMode || false,
        track_pageview: config.track_pageview || false,
        persistence: config.persistence || 'localStorage',
        api_host: config.api_host || undefined,
        
        // Session Recording Configuration - Unmask ALL content by default
        // Only mask passwords, tokens, and other truly sensitive fields
        // ✅ DISABLED auto-recording: Only record authenticated users explicitly
        record_sessions_percent: 0,  // NO automatic recording for anonymous users
        
        record_min_ms: 8000,             // Minimum recording duration before saving (SDK cap: 8000ms)
        record_idle_timeout_ms: 300000,  // Stop after 5 minutes of inactivity
        record_max_ms: 300000,            // 5-minute max per recording segment (uploads chunk, then starts fresh)
        
        record_block_class: 'mp-sensitive', // Only block elements explicitly marked as sensitive
        // Mask ONLY password fields, auth tokens, and API keys - everything else is visible
        record_mask_text_selector: 'input[type="password"], input[name*="password"], input[name*="token"], input[name*="secret"], .password, .auth-token, .api-key, [data-mask], [data-sensitive-password]',
        record_collect_fonts: false,
        
        // Additional security and privacy settings
        ip: false, // Don't track IP addresses
        property_blacklist: ['$current_url', '$referrer'], // Don't track URLs
        cross_subdomain_cookie: false,
        secure_cookie: true,
        upgrade: false,
      });

      this.isInitialized = true;
      this.isReady = true;
      
      log.info('Mixpanel initialized successfully');
    } catch (error) {
      log.error('Failed to initialize Mixpanel:', error);
      throw error;
    }
  }

  track(eventName: string, properties?: Record<string, any>): void {
    if (!this.isReady) {
      log.debug('Mixpanel not ready, skipping track:', eventName);
      return;
    }

    try {
      mixpanel.track(eventName, properties);
      log.debug('Tracked event:', eventName, properties);
    } catch (error) {
      log.error('Failed to track event:', eventName, error);
    }
  }

  identify(userId: string): void {
    if (!this.isReady) {
      log.debug('Mixpanel not ready, skipping identify:', userId);
      return;
    }

    try {
      mixpanel.identify(userId);
      log.debug('Identified user:', userId);
    } catch (error) {
      log.error('Failed to identify user:', userId, error);
    }
  }

  setUserProperties(properties: Record<string, any>): void {
    if (!this.isReady) {
      log.debug('Mixpanel not ready, skipping set user properties');
      log.warn('⚠️ [MIXPANEL EMAIL CHECK] Mixpanel NOT READY - email NOT sent!');
      return;
    }

    // 🔥 LOG EMAIL GOING TO MIXPANEL
    const hasEmail = !!properties.$email;
    log.info('📤 [MIXPANEL EMAIL CHECK] Sending to Mixpanel:', {
      hasEmail,
      emailLength: properties.$email?.length || 0,
      emailIsEmpty: properties.$email === '',
      propertyKeys: Object.keys(properties)
    });

    try {
      mixpanel.people.set(properties);
      
      if (hasEmail) {
        log.info('✅✅✅ [MIXPANEL EMAIL CHECK] EMAIL SENT TO MIXPANEL SUCCESSFULLY! ✅✅✅');
      } else {
        log.warn('⚠️⚠️⚠️ [MIXPANEL EMAIL CHECK] NO EMAIL SENT TO MIXPANEL! Properties:', Object.keys(properties));
      }
      
      log.debug('Set user properties:', properties);
    } catch (error) {
      log.error('Failed to set user properties:', error);
      log.error('❌ [MIXPANEL EMAIL CHECK] FAILED to send to Mixpanel:', error);
    }
  }

  stopRecording(): void {
    if (!this.isReady) return;
    try {
      if (typeof mixpanel.stop_session_recording === 'function') {
        mixpanel.stop_session_recording();
        log.debug('Session recording stopped');
      }
    } catch (error) {
      log.error('Failed to stop session recording:', error);
    }
  }

  reset(): void {
    if (!this.isReady) {
      log.debug('Mixpanel not ready, skipping reset');
      return;
    }

    try {
      this.stopRecording();
      mixpanel.reset();
      log.debug('Mixpanel reset completed');
    } catch (error) {
      log.error('Failed to reset Mixpanel:', error);
    }
  }

  getInitializationStatus(): boolean {
    return this.isReady;
  }

  ensureRecordingActive(): void {
    if (!this.isReady) {
      log.debug('Mixpanel not ready, skipping ensure recording');
      return;
    }

    try {
      // Check if user has opted out
      if (mixpanel.has_opted_out_tracking()) {
        log.warn('User has opted out of tracking - session recording disabled');
        return;
      }

      // Start session recording explicitly
      if (typeof mixpanel.start_session_recording === 'function') {
        mixpanel.start_session_recording();
        log.info('✅ Session recording started successfully');
      } else {
        log.debug('Session recording should be active (auto-started via record_sessions_percent)');
      }
      
      // Log session recording properties for debugging
      const sessionProps = mixpanel.get_session_recording_properties();
      if (sessionProps && Object.keys(sessionProps).length > 0) {
        log.debug('📹 Session recording properties:', sessionProps);
      }
    } catch (error) {
      log.error('Failed to ensure recording is active:', error);
    }
  }

  /**
   * Flush pending events and wait for them to send
   * Critical for events sent right before navigation to prevent event loss
   */
  async flushEvents(): Promise<void> {
    const log = createFileLogger('MIXPANEL');
    if (!this.isInitialized) {
      log.debug('Mixpanel not initialized, skipping flush');
      return;
    }
    
    return new Promise((resolve) => {
      // Mixpanel doesn't expose a flush method, so we use a small delay
      // This ensures the beacon/XHR request has time to initiate before navigation
      setTimeout(() => {
        log.debug('Events flushed - safe to navigate');
        resolve();
      }, 300);
    });
  }
}

// Create singleton instance
const mixpanelService = new MixpanelService();

// Support both named and default exports for compatibility
export { mixpanelService };
export default mixpanelService;